import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { createRoute } from "@tanstack/solid-router";
import { AlertTriangle, Check, Loader2, RotateCcw, Search, Sparkles, Type } from "@fleet/icons";
import { PageContainer, PageHeader, TextField } from "@fleet/ui";
import { appRoute } from "./route";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncQuery } from "@malla/shared/solid";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { FontCard } from "./profile/font-card";
import {
  type CatalogFont,
  FONT_CATEGORIES,
  FontDownloaderService,
  type FontWeight,
} from "@/utils/font-downloader";
import {
  getFontFamiliesQuery,
  getFontVariantsQuery,
  groupFontsWithVariants,
  queryKeys,
  VISITOR_ID,
} from "@malla/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useTypography } from "@/providers/typography-provider";
import { BRAND_FONT } from "@/config/brand-font";

function FontsPage() {
  const { t } = useI18n();
  const { db: psDb } = usePowerSyncContext();
  const { userId } = useSession();
  const drizzle = useDrizzle();
  const typography = useTypography();

  const [search, setSearch] = createSignal("");
  const [selectedCategory, setSelectedCategory] = createSignal<string>("all");
  const [downloadingId, setDownloadingId] = createSignal<string | null>(null);
  const [resetting, setResetting] = createSignal(false);

  // Current selection comes from the per-account, local-first typography provider
  // (guest-safe, instant-boot cache, pending-aware). `null` = the brand default.
  const currentFontId = () => typography.selectedFontId();

  // 2. Load all families & variants
  const familiesQuery = createPowerSyncQuery({
    queryKey: () => queryKeys.fonts.catalogFamilies(),
    db: psDb,
    queryFn: () => {
      const db = drizzle();
      return db ? getFontFamiliesQuery(db) : null;
    },
    enabled: () => Boolean(drizzle()),
  });

  const variantsQuery = createPowerSyncQuery({
    queryKey: () => queryKeys.fonts.catalogVariants(),
    db: psDb,
    queryFn: () => {
      const db = drizzle();
      return db ? getFontVariantsQuery(db) : null;
    },
    enabled: () => Boolean(drizzle()),
  });

  const fontCatalog = createMemo((): CatalogFont[] => {
    const families = familiesQuery.data ?? [];
    const variants = variantsQuery.data ?? [];
    const combined = groupFontsWithVariants(families, variants);

    return combined.map((f) => {
      const familyName = f.family ?? f.name ?? f.id;
      return {
        id: f.id,
        family: familyName,
        name: f.name ?? familyName,
        category: FONT_CATEGORIES.find((c) => c === f.category) ?? "sans-serif",
      };
    });
  });

  const dbFamilyMap = createMemo(() => {
    const families = familiesQuery.data ?? [];
    const variants = variantsQuery.data ?? [];
    return new Map(groupFontsWithVariants(families, variants).map((f) => [f.id, f]));
  });

  const filteredFonts = createMemo(() => {
    const q = search().toLowerCase().trim();
    const cat = selectedCategory();
    return fontCatalog().filter((font) => {
      const matchesSearch = q === "" || font.name.toLowerCase().includes(q);
      const matchesCategory = cat === "all" || font.category === cat;
      return matchesSearch && matchesCategory;
    });
  });

  const loadPreview = (font: CatalogFont) => {
    const dbEntry = dbFamilyMap().get(font.id);
    if (!dbEntry) {
      return;
    }
    // Only the Regular (400) weight is needed to preview the card; downloadAndLoad
    // is idempotent so this never re-fetches an already-loaded face.
    const previewVariant = dbEntry.variants.find(
      (v): v is typeof v & { weight: FontWeight; url: string } =>
        Boolean(v.weight && v.url && v.weight === "400"),
    );
    const variants = previewVariant
      ? [{ weight: previewVariant.weight, url: previewVariant.url }]
      : dbEntry.variants
          .filter((v): v is typeof v & { weight: FontWeight; url: string } =>
            Boolean(v.weight && v.url && /^[1-9]00$/.test(v.weight)),
          )
          .slice(0, 1)
          .map((v) => ({ weight: v.weight, url: v.url }));
    if (variants.length === 0) {
      return;
    }
    void FontDownloaderService.downloadAndLoad(font.family, variants);
  };

  const handleSelect = async (font: CatalogFont) => {
    const uid = userId();
    const db = drizzle();
    if (!uid || !db || uid === VISITOR_ID || font.id === currentFontId()) {
      return;
    }

    try {
      setDownloadingId(font.id);
      const dbEntry = dbFamilyMap().get(font.id);
      if (dbEntry) {
        const variants = dbEntry.variants
          .filter((v): v is typeof v & { weight: FontWeight; url: string } =>
            Boolean(v.weight && v.url && /^[1-9]00$/.test(v.weight)),
          )
          .map((v) => ({ weight: v.weight, url: v.url }));

        await FontDownloaderService.downloadAndLoad(font.family, variants);
      }

      // Persist per-account (synced + local cache) + apply live via the provider.
      await typography.setFont({ id: font.id, name: font.family });
      const { name } = font;
      showToast(t(msg`Font applied: ${name}`), "success");
    } catch (err) {
      console.error("[Typography] Failed to apply font:", err);
      showToast(t(msg`Error applying font`), "error");
    } finally {
      setDownloadingId(null);
    }
  };

  // Reset to the app's BUNDLED brand font. Catalog-independent: works even with an
  // empty/offline catalog — it just clears the runtime override.
  const handleReset = async () => {
    if (resetting() || currentFontId() === null) {
      return;
    }
    try {
      setResetting(true);
      await typography.resetFont();
      const label = typography.brandLabel;
      showToast(t(msg`Reset to default font: ${label}`), "success");
    } catch (err) {
      console.error("[Typography] Failed to reset font:", err);
      showToast(t(msg`Error resetting font`), "error");
    } finally {
      setResetting(false);
    }
  };

  const categories = ["all", ...FONT_CATEGORIES];

  return (
    <PageContainer maxWidth="4xl" class="space-y-8">
      {/* Header */}
      <PageHeader
        title={t(msg`Premium Typography`)}
        description={t(msg`Customize your application's global typography for a unique look.`)}
        backTo="/profile"
        icon={Type}
      />

      {/* Notice: the account's chosen font is no longer available (removed from the
          catalog) → the app's brand default is applied and shown as selected. */}
      <Show when={typography.isSelectedUnavailable()}>
        {(_) => {
          const missing = typography.unavailableFontName();
          const brandLabel = BRAND_FONT.label;
          return (
            <div
              data-testid="font-unavailable-notice"
              class="flex items-start gap-3 rounded-2xl border border-warning/30 bg-warning/10 p-4 text-sm md:px-6"
            >
              <AlertTriangle class="mt-0.5 h-5 w-5 shrink-0 text-warning" />
              <p class="text-foreground/90">
                {missing
                  ? t(
                      msg`The font "${missing}" you had selected is no longer available. Your app default (${brandLabel}) is now applied.`,
                    )
                  : t(
                      msg`Your selected font is no longer available. Your app default (${brandLabel}) is now applied.`,
                    )}
              </p>
            </div>
          );
        }}
      </Show>

      {/* Active font + reset-to-brand. The reset target is the app's BUNDLED brand
          font (always present); resetting works even with an empty/offline catalog. */}
      <div class="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/20 bg-surface/40 p-4 md:px-6">
        <div class="flex flex-col gap-0.5">
          <span class="text-xs uppercase tracking-wide text-muted-foreground">
            {t(msg`Current font`)}
          </span>
          <span data-testid="font-current-label" class="text-base font-semibold">
            <Show when={currentFontId()} fallback={typography.brandLabel}>
              {typography.fontFamilyName() ?? typography.brandLabel}
            </Show>
            <Show when={!currentFontId()}>
              <span class="ml-2 text-xs font-normal text-muted-foreground">
                {t(msg`(app default)`)}
              </span>
            </Show>
          </span>
        </div>
        <Button
          data-testid="font-reset-button"
          variant="outline"
          size="sm"
          class="gap-2"
          disabled={!currentFontId() || resetting()}
          onClick={() => void handleReset()}
        >
          <Show when={resetting()} fallback={<RotateCcw class="h-4 w-4" />}>
            <Loader2 class="h-4 w-4 animate-spin" />
          </Show>
          {t(msg`Reset to default`)}
        </Button>
      </div>

      {/* Filters & Search */}
      <div class="flex flex-col gap-4 bg-primary/5 border border-primary/10 rounded-2xl p-4 md:p-6 backdrop-blur-md">
        <div class="relative">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <TextField
            placeholder={t(msg`Search fonts by name...`)}
            class="pl-10 h-11 bg-background/50 border-border/20"
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
          />
        </div>

        <div class="flex flex-wrap gap-2">
          <For each={categories}>
            {(cat) => (
              <Button
                variant={selectedCategory() === cat ? "default" : "outline"}
                size="sm"
                class="rounded-full capitalize text-xs px-4"
                onClick={() => setSelectedCategory(cat)}
              >
                {cat === "all" ? t(msg`All`) : cat}
              </Button>
            )}
          </For>
        </div>
      </div>

      {/* Font List */}
      <Show
        when={!familiesQuery.isLoading && !variantsQuery.isLoading}
        fallback={
          <div class="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 class="h-10 w-10 text-primary animate-spin" />
            <p class="text-muted-foreground animate-pulse text-sm">{t(msg`Loading catalog...`)}</p>
          </div>
        }
      >
        <div class="grid gap-4 md:grid-cols-2">
          {/* App default (brand) — a first-class, always-available SELECTABLE option
              (not just a reset button): picking it returns to the bundled brand font.
              Hidden while searching so it doesn't pollute name matches. */}
          <Show when={search().trim() === ""}>
            <div
              data-testid="font-default-card"
              // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- styled container with button semantics via role + keyboard handlers.
              role="button"
              tabindex={0}
              aria-pressed={currentFontId() === null}
              aria-label={(() => {
                const brandLabel = BRAND_FONT.label;
                return t(msg`Use the app default font ${brandLabel}`);
              })()}
              onClick={() => void handleReset()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  void handleReset();
                }
              }}
              class={`cursor-pointer overflow-hidden rounded-xl border p-5 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                currentFontId() === null
                  ? "border-primary bg-primary/5 shadow-md shadow-primary/5"
                  : "border-border/10 bg-card hover:border-primary/40 hover:shadow-lg"
              }`}
            >
              <div class="flex items-start justify-between gap-4">
                <div>
                  <h3 class="flex items-center gap-1.5 text-lg font-bold">
                    {BRAND_FONT.label}
                    <Show when={currentFontId() === null}>
                      <span class="rounded-full bg-primary p-0.5 text-primary-foreground">
                        <Check class="h-3 w-3" />
                      </span>
                    </Show>
                  </h3>
                  <span class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    {t(msg`App default`)}
                  </span>
                </div>
                <span class="flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
                  <Sparkles class="h-3.5 w-3.5" />
                  {t(msg`Brand`)}
                </span>
              </div>
              <div
                class="mt-4 flex h-24 items-center overflow-hidden rounded-xl border border-border/5 bg-secondary/10 p-4"
                style={{
                  "font-family": `'${BRAND_FONT.family}', ui-sans-serif, system-ui, sans-serif`,
                }}
              >
                <div class="space-y-1">
                  {/* oxlint-disable-next-line lingui/no-unlocalized-strings -- font preview text */}
                  <p class="text-xl font-bold">Aa Bb Cc 123</p>
                  <p class="text-xs text-muted-foreground">
                    {t(msg`The quick brown fox jumps over the lazy dog.`)}
                  </p>
                </div>
              </div>
            </div>
          </Show>
          <For
            each={filteredFonts()}
            fallback={
              <div class="col-span-2 text-center py-16 border border-dashed rounded-2xl text-muted-foreground">
                {t(msg`No fonts found matching your search.`)}
              </div>
            }
          >
            {(font) => (
              <FontCard
                font={font}
                isSelected={font.id === currentFontId()}
                isDownloading={downloadingId() === font.id}
                onSelect={handleSelect}
                onVisible={loadPreview}
              />
            )}
          </For>
        </div>
      </Show>
    </PageContainer>
  );
}

export const profileFontsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile/fonts",
  component: FontsPage,
});
